/**
 * Reading the desktop release metadata that the publish pipeline writes to each environment's
 * public `releases.<host>` bucket (`Backend/desktop/scripts/release-desktop-assets.ts` produces it;
 * `docs/desktop-release-pipeline.md` describes the bucket layout).
 *
 * The metadata is split one file per platform — `download-mac.json` holds `{mac: {...}}` and
 * `download-windows.json` holds `{windows: {...}}` — so each file has exactly one writer and a
 * single-platform rollback can restore one without disturbing the other. Both root files carry a
 * 60-second `Cache-Control`, and artifact URLs are absolute and version-pinned, so a consumer reads
 * whichever release is current rather than deriving a filename.
 */

export const desktopReleasePlatforms = ['mac', 'windows'] as const

export type DesktopReleasePlatform = (typeof desktopReleasePlatforms)[number]

/**
 * The `DesktopReleasePlatform` a URL path segment names, or `undefined` for anything else. The
 * download permalink turns `/apps/<segment>` into a platform with this rather than trusting the
 * segment, so an unknown platform is a 404 instead of a lookup against a key that can't resolve.
 *
 * Matched against the list the type is derived from, so a platform added to one is recognised here
 * rather than silently rejected by a literal comparison that still type-checks.
 */
export function parseDesktopReleasePlatform(value: string): DesktopReleasePlatform | undefined {
  return desktopReleasePlatforms.find((platform) => platform === value)
}

/**
 * The bucket that serves a site's desktop releases, derived the same way CI derives the publish
 * target (`DESKTOP_RELEASE_HOST=releases.$HOST`), so a berry reads the builds that berry published
 * and production reads production's.
 *
 * Returns `undefined` for any host without a releases bucket — notably `localhost` in development —
 * so callers fall back rather than waiting on a request that cannot resolve.
 */
export function desktopReleasesOrigin(siteUrl: string): string | undefined {
  let hostname: string
  try {
    hostname = new URL(siteUrl).hostname
  } catch {
    return undefined
  }

  // The bucket is named after the bare apex, so `www` never appears in the releases host.
  const host = hostname.replace(/^www\./, '')
  if (host !== 'notability.com' && !host.endsWith('.notability.com')) {
    return undefined
  }

  return `https://releases.${host}`
}

export function desktopDownloadMetadataUrl(releasesOrigin: string, platform: DesktopReleasePlatform): string {
  return `${releasesOrigin}/desktop/download-${platform}.json`
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}

/**
 * The artifact URL from one entry, or `undefined` if it isn't one this page will link to.
 *
 * `releasesOrigin` is the bucket the document was read from, and the URL must sit on it. The
 * publisher builds these from its own `DESKTOP_RELEASE_HOST`, so a mismatch means a job published
 * into one bucket a document pointing at another — a misconfiguration worth dropping the link over,
 * given the value ends up as an href the visitor clicks to run an installer.
 *
 * The parsed `href` is returned rather than the raw string, so the URL handed out is the one the
 * origin check ran against — a document writing `https://releases.notability.com:443/x` otherwise
 * passes the check and still ships the visitor the unnormalized form.
 */
function entryUrl(value: unknown, releasesOrigin: string): string | undefined {
  if (!isRecord(value) || typeof value.url !== 'string') {
    return undefined
  }
  try {
    const url = new URL(value.url)
    return url.protocol === 'https:' && url.origin === releasesOrigin ? url.href : undefined
  } catch {
    return undefined
  }
}

/**
 * Picks the artifact to offer a visitor from one platform's metadata document.
 *
 * Prefers the `universal` build, which runs on every architecture. Failing that, only a lone `x64`
 * artifact is offered: x64 also runs on ARM (Rosetta 2 on macOS, emulation on Windows), so it is the
 * one architecture-specific build that can't strand a visitor. A lone `arm64` artifact yields
 * `undefined`, because the reverse doesn't hold — an arm64 build simply won't launch on an Intel Mac
 * or an x64 PC. So do two or more architecture-specific artifacts with no universal build: these
 * surfaces detect the OS but not the architecture, so picking one would hand a share of that
 * platform's visitors a binary they can't run. The publisher takes the same position, refusing to
 * label a single-architecture build `universal` for exactly this reason.
 */
export function downloadUrlFromMetadata(
  platform: DesktopReleasePlatform,
  releasesOrigin: string,
  metadata: unknown
): string | undefined {
  if (!isRecord(metadata)) {
    return undefined
  }
  const platformEntries = metadata[platform]
  if (!isRecord(platformEntries)) {
    return undefined
  }

  const universal = entryUrl(platformEntries.universal, releasesOrigin)
  if (universal !== undefined) {
    return universal
  }

  // An `arm64` artifact present at all disqualifies the platform: on its own it strands Intel
  // visitors, and alongside `x64` it leaves nobody to choose between them, since these surfaces
  // detect the OS but not the architecture.
  return entryUrl(platformEntries.arm64, releasesOrigin) === undefined
    ? entryUrl(platformEntries.x64, releasesOrigin)
    : undefined
}
